import gql from 'graphql-tag';

const ForumReviewFragment = gql`
  fragment ForumReview on ForumReview {
    id
    createdAt
    updatedAt
    deletedAt
    content
    images
    postId
    toPostId
    topicId
    reviewCount
    forumCustomer {
      headPortrait
      name
    }
    reviewUid
    parentForumCustomer {
      headPortrait
      name
    }
    upVotes
    reviewTime
    level
    upVoted
    parentPostId
  }
`;

const ForumTagFragment = gql`
  fragment ForumTag on ForumTag {
    id
    createdAt
    updatedAt
    deletedAt
    name
    remark
    description
    image
    smallProgramQRCodeLink
    status
    postCount
    replyCount
    maxUpvoteCount
    shareImg
    shareTitle
    forumTagActivity {
      id
      createdAt
      updatedAt
      deletedAt
      name
      remark
      description
      smallProgramQRCodeLink
      status
      startTime
      endTime
      forumTag {
        id
      }
      forumTagId
      postCount
      replyCount
      maxUpvoteCount
    }
  }
`;

const ProductFragment = gql`
  fragment Product on Product {
    id
    name
    enabled
    productTotalStock
    pointsExchangeInfo {
      id
      allowPurchaseAtOriginalPrice
      exchangeConditionType
      pointExchange {
        points
        cash
        exchangeTotal
      }
    }
    productPurchasePermission {
      id
      productId
      isMembershipPlanPurchase
      membershipPlans {
        id
      }
      guideMembershipPlanId
    }
    memberPriceActivityAmount {
      minDiscount
      maxDiscount
      minMemberPriceAmount
      maxMemberPriceAmount
      memberPriceProductVariant {
        memberPriceAmount
        productVariantId
        memberDiscount
        discountType
      }
    }
    featuredAsset {
      source
    }
    customFields {
      price
      markingPrice
      limitType
      limitCount
      putOnSaleType
      putOnSaleTime
      isVipProduct
      isHiddenCart
      virtualTargetType
      productType
    }
    participatingActivities {
      id
      createdAt
      updatedAt
      endsAt
      couponCode
      perCustomerUsageLimit
      enabled
      customFields {
        type
        activityName
      }
    }
  }
`;

const ForumPostFragment = gql`
  fragment ForumPost on ForumPost {
    id
    createdAt
    updatedAt
    deletedAt
    type
    title
    content
    images
    mainImage
    status
    releaseTime
    auditTime
    refuseReason
    forumWishPost {
      id
      createdAt
      updatedAt
      deletedAt
      type
      title
      content
      images
      mainImage
      status
      releaseTime
      auditTime
      refuseReason
      tid
      isWish
      downVotes
      upVotes
      votes
      viewCount
      mainPid
      postCount
      tags
      upVoted
      nodeBBPostId
      uid
    }
    forumCustomer {
      name
      headPortrait
      phone
      createType
    }
    forumCustomerId
    forumTags {
      ...ForumTag
    }
    forumActivities {
      id
      createdAt
      updatedAt
      deletedAt
      name
      remark
      description
      smallProgramQRCodeLink
      status
      startTime
      endTime
      forumTag {
        ...ForumTag
      }
      forumTagId
    }
    products {
      ...Product
    }
    tid
    isWish
    downVotes
    upVotes
    votes
    viewCount
    mainPid
    postCount
    tags
    upVoted
    nodeBBPostId
    uid
    shareCount
    shareImg
    shareTitle
  }
  ${ForumTagFragment}
  ${ProductFragment}
`;

/**
 * 获取获取话题
 */
export const fetchForumTagsDocument = gql`
  query forumTags($options: ForumTagListOptions) {
    forumTags(options: $options) {
      items {
        ...ForumTag
      }
      totalItems
    }
  }
  ${ForumTagFragment}
`;

/**
 * 获取话题详情
 */
export const fetchForumTagDocument = gql`
  query forumTag($forumTagId: ID!) {
    forumTag(forumTagId: $forumTagId) {
      ...ForumTag
    }
  }
  ${ForumTagFragment}
`;

/**
 * 发帖
 */
export const upsertForumPostDocument = gql`
  mutation upsertForumPost($input: ForumPostInput!) {
    upsertForumPost(input: $input) {
      ...ForumPost
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取待审核帖子
 */
export const fetchUnAuditForumPostsDocument = gql`
  query getUnAuditForumPosts($options: ForumPostListOptions) {
    getUnAuditForumPosts(options: $options) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取热门帖子
 */
export const fetchHotForumPostsDocument = gql`
  query getHotForumPosts(
    $options: ForumPostListOptions
    $forumPostHotDateType: ForumPostHotDateType!
    $isWish: Boolean
  ) {
    getHotForumPosts(
      options: $options
      forumPostHotDateType: $forumPostHotDateType
      isWish: $isWish
    ) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取最新帖子
 */
export const fetchNewForumPostsDocument = gql`
  query getNewForumPosts($options: ForumPostListOptions) {
    getNewForumPosts(options: $options) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取帖子详情
 */
export const fetchForumPostDocument = gql`
  query getForumTopicByPid($id: String!) {
    getForumTopicByPid(id: $id) {
      ...ForumPost
    }
  }
  ${ForumPostFragment}
`;

/**
 * 帖子取消点赞
 */
export const forumDownPostDocument = gql`
  mutation forumDownPost($pid: String!) {
    forumDownPost(pid: $pid) {
      upVoted
    }
  }
`;

/**
 * 帖子点赞
 */
export const forumUpPostDocument = gql`
  mutation forumUpPost($pid: String!) {
    forumUpPost(pid: $pid) {
      upVoted
    }
  }
`;

/**
 * 帖子评论
 */
export const forumReviewPostDocument = gql`
  mutation forumReviewPost($reviewId: ID, $postId: ID, $content: String!, $images: [String]) {
    forumReviewPost(reviewId: $reviewId, postId: $postId, content: $content, images: $images) {
      ...ForumReview
    }
  }
  ${ForumReviewFragment}
`;

/**
 * 获取帖子评论
 */
export const fetchForumReviewsDocument = gql`
  query getForumReviews($pid: String!, $level: Int!, $options: ForumReviewListOptions) {
    getForumReviews(pid: $pid, level: $level, options: $options) {
      items {
        ...ForumReview
      }
      totalItems
    }
  }
  ${ForumReviewFragment}
`;

/**
 * 根据话题获取热门帖子
 */
export const fetchHotForumPostsByTagDocument = gql`
  query getHotForumPostsByTag(
    $options: ForumPostListOptions
    $forumTagId: ID!
    $forumPostHotDateType: ForumPostHotDateType!
  ) {
    getHotForumPostsByTag(
      options: $options
      forumTagId: $forumTagId
      forumPostHotDateType: $forumPostHotDateType
    ) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 根据话题获取最新帖子
 */
export const fetchNewForumPostsByTagDocument = gql`
  query getNewForumPostsByTag($options: ForumPostListOptions, $forumTagId: ID!) {
    getNewForumPostsByTag(options: $options, forumTagId: $forumTagId) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取帖子通知
 */
export const fetchForumNotificationsDocument = gql`
  query forumNotifications(
    $notificationType: NotificationType!
    $options: ForumNotificationListOptions
  ) {
    forumNotifications(notificationType: $notificationType, options: $options) {
      items {
        bodyLong
        bodyShort
        datetime
        datetimeISO
        from
        image
        pid
        read
        topicTitle
        type
        forumCustomer {
          id
          name
          headPortrait
        }
        reviewId
        forumPostId
      }
      totalItems
    }
  }
`;

/**
 * 删除帖子
 */
export const deleteForumPostDocument = gql`
  mutation deleteForumPost($forumPostId: ID!) {
    deleteForumPost(forumPostId: $forumPostId) {
      message
    }
  }
`;

/**
 * 获取我的全部帖子
 */
export const fetchMyForumPostsDocument = gql`
  query getMyForumPosts($options: ForumPostListOptions) {
    getMyForumPosts(options: $options) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取我的全部点赞数和评论数
 */
export const fetchMyForumPostCountDocument = gql`
  query getMyForumPostCount {
    getMyForumPostCount {
      topicCount
      postCount
      reputation
    }
  }
`;

/**
 * 获取帖子作者信息
 */
export const getForumCustomerDocument = gql`
  query getForumCustomer {
    getForumCustomer {
      forumUid
      customerId
    }
  }
`;

/**
 * 标记通知为已读
 */
export const markNotificationsAsReadDocument = gql`
  mutation markNotificationsAsRead($notificationType: NotificationType!) {
    markNotificationsAsRead(notificationType: $notificationType)
  }
`;

/**
 * 是否有未读通知
 */
export const hasUnreadNotificationsDocument = gql`
  query hasUnreadNotifications($notificationType: NotificationType!) {
    hasUnreadNotifications(notificationType: $notificationType)
  }
`;

/**
 * 获取活动排行榜帖子 - 最热
 */
export const getHotForumPostsByActivityDocument = gql`
  query getHotForumPostsByActivity(
    $options: ForumPostListOptions
    $activityId: ID!
    $forumPostHotDateType: ForumPostHotDateType
  ) {
    getHotForumPostsByActivity(
      options: $options
      activityId: $activityId
      forumPostHotDateType: $forumPostHotDateType
    ) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 获取活动排行榜帖子 - 最新
 */
export const fetchNewForumPostsByActivityDocument = gql`
  query getNewForumPostsByActivity(
    $options: ForumPostListOptions
    $activityId: ID!
    $isWish: Boolean
  ) {
    getNewForumPostsByActivity(options: $options, activityId: $activityId, isWish: $isWish) {
      items {
        ...ForumPost
      }
      totalItems
    }
  }
  ${ForumPostFragment}
`;

/**
 * 帖子分享上报
 */
export const forumPostShareDocument = gql`
  mutation forumPostShare($id: ID!) {
    forumPostShare(id: $id) {
      id
    }
  }
`;
